<template>
  <sfs-base-table
    :data-provider="$workOrdersAPI.getWorkOrders"
    provider-key="workOrders"
    :columns="translatedColumns(modelName, tableColumns)"
    :filters="filters"
    :current-page="currentPage"
    :per-page="perPage"
    :show-per-page="showPerPage"
    @data-changed="onRecordsChanged"
  >
    <template #head(checkbox)>
      <b-form-checkbox
        class="checkbox-header"
        :checked="isAllSelected"
        @change="isAllSelected ? clearAllRows() : selectAllRows()"
      />
    </template>
    <template #cell(checkbox)="data">
      <b-form-checkbox
        class="checkbox"
        v-model="selectedWorkOrders"
        :value="data.item"
      />
    </template>
    <template #cell(number)="data">
      <b-link :to="{ name: 'work-order', params: { id: data.item.id } }">
        {{ data.item.number }}
      </b-link>
    </template>
    <template #cell(description)="data">
      <text-with-popover
        v-if="data.item.description != data.item.service?.name"
        :popover-id="`work-order-${data.item.id}-description`"
        :text="data.item.description"
      />
      <b-link
        v-if="data.item.service"
        class="font-size-sm text-prewrap"
        :to="{ name: 'service', params: { id: data.item.service.id } }"
      >
        {{ data.item.service.name }}
      </b-link>
    </template>
    <template #cell(date_bucket)="data">
      <div class="text-nowrap">
        {{ data.item | dateRangeFormat("date_bucket_start", "date_bucket_end", data.item.location.time_zone) }}
        <div
          v-if="data.item.completion_source"
          class="font-size-sm text-secondary"
        >
          Source: {{ data.item.completion_source | humanizedFormat | capitalizeFormat }}
        </div>
      </div>
    </template>
    <template #cell(location_id)="data">
      <b-link
        :to="{ name: 'location', params: { id: data.item.location_id } }"
        v-b-tooltip.hover.window
        :title="`${data.item.location.street}, ${data.item.location.city}, ${data.item.location.state}, ${data.item.location.zip_code}`"
      >
        {{ data.item.location.store_number }}
        <span v-if="data.item.location.name && data.item.location.name != data.item.location.store_number">- {{ data.item.location.name }}</span>
      </b-link>
      <div class="text-nowrap font-size-sm text-secondary">
        {{ { city: data.item.location.city, state: data.item.location.state } | locationFormat }}
      </div>
    </template>
    <template #cell(client_id)="data">
      <b-link :to="{ name: 'client', params: { id: data.item.client.id } }">
        {{ data.item.client.name }}
      </b-link>
    </template>
    <template #cell(service_id)="data">
      <b-link
        v-if="data.item.service"
        :to="{ name: 'service', params: { id: data.item.service.id } }"
      >
        {{ data.item.service.name }}
      </b-link>
    </template>
    <template #cell(parent_id)="data">
      <b-link
        v-if="data.item.parent_id"
        :to="{ name: 'work-order', params: { id: data.item.parent_id } }"
      >
        {{ data.item.parent_number }}
      </b-link>
    </template>
    <template #cell(date_bucket_start)="data">
      {{ data.item.date_bucket_start | dateFormat }}
    </template>
    <template #cell(date_bucket_end)="data">
      {{ data.item.date_bucket_end | dateFormat }}
    </template>
    <template #cell(checked_in_date)="data">
      <span v-if="data.item.checked_in_at">
        {{ data.item.checked_in_at | dateTimeFormat(data.item.location.time_zone) }}
      </span>
    </template>
    <template #cell(checked_out_date)="data">
      <span v-if="data.item.checked_out_at">
        {{ data.item.checked_out_at | dateTimeFormat(data.item.location.time_zone) }}
      </span>
    </template>
    <template #cell(assigned_to_id)="data">
      <b-link
        v-if="data.item.assigned_to"
        :to="{ name: 'user', params: { id: data.item.assigned_to.id } }"
      >
        {{ data.item.assigned_to.name }}
      </b-link>
      <br>
      <span
        v-if="data.item.done_by && data.item.done_by_id != data.item.assigned_to_id"
        class="font-size-sm"
      >
        <b-link :to="{ name: 'user', params: { id: data.item.done_by.id } }">
          {{ data.item.done_by.name }}
        </b-link>
      </span>
    </template>
    <template #cell(done_by_id)="data">
      <b-link
        v-if="data.item.done_by_id"
        :to="{ name: 'user', params: { id: data.item.done_by.id } }"
      >
        {{ data.item.done_by.name }}
      </b-link>
    </template>
    <template #cell(status)="data">
      <status-badge
        :status="data.item.status"
        :sub-status="data.item.sub_status"
        :status-variants="statusVariants"
      />
      <span
        v-if="data.item.cancellation_reason"
        class="font-size-sm text-secondary"
      >
        {{ data.item.cancellation_reason | humanizedFormat | capitalizeFormat }}
      </span>
    </template>
    <template #cell(approved_at)="data">
      <span v-if="data.item.approved_at">
        {{ data.item.approved_at | dateTimeFormat }}
      </span>
    </template>
    <template #cell(latest_note)="data">
      <text-with-popover
        v-if="data.item.latest_note"
        :popover-id="`work-order-${data.item.id}-latest-note`"
        :text="data.item.latest_note"
      />
    </template>
    <template #cell(latest_note_at)="data">
      <span v-if="data.item.latest_note_at">
        {{ data.item.latest_note_at | dateTimeFormat(data.item.location.time_zone) }}
      </span>
    </template>
    <template #cell(capex)="data">
      <span v-if="data.item.capex">Yes</span>
      <span v-else>No</span>
    </template>
    <template #cell(tags)="data">
      <b-badge
        v-for="tag in data.item.tags"
        :key="tag"
        variant="secondary"
        class="font-size-sm mr-1 mb-1"
      >
        {{ tag }}
      </b-badge>
    </template>
    <template #cell(check_in_distance)="data">
      <div class="text-nowrap">
        <b-badge
          v-if="!['', undefined, null].includes(data.item.check_in_distance)"
          :variant="data.item.check_in_distance <= 1 ? 'success' : 'danger'"
        >
          {{ data.item.check_in_distance }} mi
        </b-badge>
      </div>
    </template>
    <template #cell(manager_id)="data">
      <div v-if="data.item.manager">
        <b-link :to="{ name: 'user', params: { id: data.item.manager.id } }">
          {{ data.item.manager.name }}
        </b-link>
      </div>
      <div v-else-if="data.item.client_managers && data.item.client_managers.length">
        <span
          v-for="(manager, index) in data.item.client_managers"
          :key="manager.id"
        >
          <b-link :to="{ name: 'user', params: { id: manager.id } }">
            {{ manager.name }}
          </b-link><span v-if="index !== data.item.client_managers.length - 1">, </span>
        </span>
      </div>
    </template>
    <template #cell(cost)="data">
      <money-format
        :value="data.item.cost.amount"
        :currency-code="data.item.cost.currency"
        subunits-value
      />
    </template>
    <template #cell(price)="data">
      <money-format
        v-if="$can('read_price', 'WorkOrderCharge')"
        :value="data.item.price.amount"
        :currency-code="data.item.price.currency"
        subunits-value
      />
    </template>
    <template #cell(open_duration)="data">
      <span
        v-if="openFor(data.item).text"
        class="d-inline-flex align-items-center text-nowrap"
      >
        <svg
          v-if="openFor(data.item).isOpen"
          xmlns="http://www.w3.org/2000/svg"
          width="35"
          height="35"
          fill="currentColor"
          class="flex-shrink-0"
          :class="openFor(data.item).textClass"
          viewBox="0 0 8 16"
        >
          <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
        </svg>
        <span
          class="open-for-time font-size-sm"
          :class="[
            openFor(data.item).textClass,
            openFor(data.item).isOpen ? 'font-w600' : 'font-w400',
          ]"
        >
          {{ openFor(data.item).text }}
        </span>
      </span>
    </template>
    <template #cell(square_footage)="data">
      <div
        v-if="data.item.location.stock_room_footage"
        class="text-nowrap"
      >
        Stock Room: {{ data.item.location.stock_room_footage }} sq. ft
      </div>
      <div
        v-if="data.item.location.sales_floor_footage"
        class="text-nowrap"
      >
        Sales Floor: {{ data.item.location.sales_floor_footage }} sq. ft
      </div>
    </template>
  </sfs-base-table>
</template>

<script>
import tablePropsMixin from '@/mixins/tablePropsMixin';
import translationMixin from '@/mixins/translationMixin';
import { WORK_ORDER_TABLE_DEFAULT_COLUMNS, WORK_ORDER_MODEL_NAME, STATUS_VARIANTS } from '@/constants/workOrders';
import { DateTime } from 'luxon';
import StatusBadge from '@/components/shared/StatusBadge.vue';
import TextWithPopover from '@/components/shared/TextWithPopover.vue';
import { getWorkOrderOpenDuration } from '@/utils/workOrderOpenDuration';

export default {
  name: 'WorkOrdersTable',
  components: {
    StatusBadge,
    TextWithPopover,
  },
  mixins: [tablePropsMixin, translationMixin],
  props: {
    columns: {
      type: Array,
      default: WORK_ORDER_TABLE_DEFAULT_COLUMNS,
    },
  },
  data() {
    return {
      modelName: WORK_ORDER_MODEL_NAME,
      statusVariants: STATUS_VARIANTS,
      selectedWorkOrders: [],
      records: [],
      now: DateTime.now(),
      nowTimer: null,
    };
  },
  computed: {
    tableColumns() {
      const checkboxColumn = { key: 'checkbox', label: '' };
      const hasCheckbox = this.columns?.some((column) => column.key === checkboxColumn.key);
      return hasCheckbox ? this.columns : [checkboxColumn, ...this.columns];
    },
    isAllSelected() {
      return this.records.length > 0 && this.selectedWorkOrders.length === this.records.length;
    },
  },
  watch: {
    selectedWorkOrders(newValue) {
      this.$emit('selected-changed', newValue);
    },
  },
  mounted() {
    this.nowTimer = setInterval(() => {
      this.now = DateTime.now();
    }, 1000);
  },
  beforeDestroy() {
    clearInterval(this.nowTimer);
  },
  methods: {
    openFor(workOrder) {
      return getWorkOrderOpenDuration(workOrder, this.now);
    },
    onRecordsChanged(records) {
      this.records = records || [];
      if (this.selectedWorkOrders.length) {
        this.clearAllRows();
      }
    },
    selectAllRows() {
      this.selectedWorkOrders = [...this.records];
    },
    clearAllRows() {
      this.selectedWorkOrders = [];
    },
    clearSelection() {
      this.clearAllRows();
    },
  },
};
</script>

<style scoped>
.open-for-time {
  font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>
